import { type IntlShape } from 'react-intl'

import { useFormatPrice } from '@webapp/core/hooks/useFormatPrice'
import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import { type GlobalSearchResultTypeEnum } from '../../../../../../value-objects/enums'
import { type GlobalSearchItemMetadata } from '../../../../../../value-objects/GlobalSearchItemMetadata'
import { useFormatGlobalSearchDateLabel } from '../../hooks/useFormatGlobalSearchDateLabel'

export const EXTERNAL_PAYMENT_METHOD = 'external'

function formatInvoiceNumber(
  metadata: GlobalSearchItemMetadata,
  intl: IntlShape
): Nullable<string> {
  if ('invoiceNumber' in metadata && metadata.invoiceNumber) {
    return intl.formatMessage(messages.invoiceNumberLabel, {
      invoiceNumber: metadata.invoiceNumber,
    })
  }
  return null
}

function formatPaymentMethod(
  metadata: GlobalSearchItemMetadata,
  intl: IntlShape
): Nullable<string> {
  if ('vendorPaymentMethod' in metadata && metadata.vendorPaymentMethod) {
    return metadata.vendorPaymentMethod === EXTERNAL_PAYMENT_METHOD
      ? intl.formatMessage(messages.externalPaymentMethodLabel)
      : metadata.vendorPaymentMethod
  }
  return null
}

function formatAmount(
  metadata: GlobalSearchItemMetadata,
  intl: IntlShape,
  formatPrice: ReturnType<typeof useFormatPrice>
): Nullable<string> {
  if ('amount' in metadata && metadata.amount) {
    return intl.formatMessage(messages.amountLabel, {
      amount: formatPrice(metadata.amount.value),
      currency: metadata.amount.currencyName,
    })
  }
  return null
}

function extractVendorName(
  metadata: GlobalSearchItemMetadata
): Nullable<string> {
  if ('vendorName' in metadata) {
    return metadata.vendorName
  }
  return null
}

function extractSku(metadata: GlobalSearchItemMetadata): Nullable<string> {
  if ('sku' in metadata) {
    return metadata.sku
  }
  return null
}

function extractExternalId(
  metadata: GlobalSearchItemMetadata
): Nullable<string> {
  if ('externalId' in metadata) {
    return metadata.externalId
  }
  return null
}

function formatDate(
  type: GlobalSearchResultTypeEnum,
  metadata: GlobalSearchItemMetadata,
  formatDateLabel: ReturnType<typeof useFormatGlobalSearchDateLabel>
): Nullable<string> {
  if ('date' in metadata && metadata.date) {
    return formatDateLabel(type, metadata.date)
  }
  return null
}

export const useGlobalSearchListItemMetadata = () => {
  const intl = useProcurifyIntl()
  const formatDateLabel = useFormatGlobalSearchDateLabel()
  const formatPrice = useFormatPrice()

  return (
    type: GlobalSearchResultTypeEnum,
    metadata: GlobalSearchItemMetadata
  ): Nullable<string> => {
    const parts = [
      extractVendorName(metadata),
      formatInvoiceNumber(metadata, intl),
      formatPaymentMethod(metadata, intl),
      extractSku(metadata),
      extractExternalId(metadata),
      formatDate(type, metadata, formatDateLabel),
      formatAmount(metadata, intl, formatPrice),
    ].filter((part): part is string => part !== null)

    if (parts.length === 0) {
      return null
    }
    if (parts.length === 1) {
      return parts[0]
    }
    if (parts.length === 2) {
      return intl.formatMessage(messages.twoPartLabel, {
        first: parts[0],
        second: parts[1],
      })
    }
    return intl.formatMessage(messages.threePartLabel, {
      first: parts[0],
      second: parts[1],
      third: parts[2],
    })
  }
}
